import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from './ScrollReveal';
import { SubtleStars } from './AtmosphericGrain';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ExternalLink, FileText, Video } from 'lucide-react';
import { SketchArrow } from './scrapbook/SketchArrow';

export function CinematicProject({ project, index }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Parallax effects - smoother, less aggressive
  const questionY = useTransform(scrollYProgress, [0, 0.5], [60, -30]);
  const imageY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const imageScale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.95, 1, 1, 0.98]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.7]);

  // Project-specific visual treatments
  const getProjectStyle = () => {
    switch (project.title) {
      case 'Computational Fabrics for Athletes':
        return {
          showStars: true,
          lightPosition: 'top-right',
          imageSize: 'large',
        };
      case 'Audio Description for Accessibility':
        return {
          minimal: true,
          lightPosition: 'center',
          imageSize: 'medium',
        };
      case 'Gumiho':
        return {
          playful: true,
          lightPosition: 'bottom-left',
          imageSize: 'large',
        };
      default:
        return {
          lightPosition: 'top-left',
          imageSize: 'large',
        };
    }
  };

  const style = getProjectStyle();

  // Depth-based atmospheric elements - enhanced near beginning only
  const getDepthAtmosphere = () => {
    switch (index) {
      case 0: // Ocean Pledge - shallow water, enhanced light rays and blue haze
        return {
          lightRays: true,
          lightRayOpacity: 0.045,
          lightRayCount: 6,
          depthHaze: 'from-slate-900/8 to-transparent',
          ambientParticles: 4,
          blueGlow: true,
          blueGlowOpacity: 0.08,
        };
      case 1: // Computational Fabrics - fading effects, minimal rays
        return {
          lightRays: true,
          lightRayOpacity: 0.015,
          lightRayCount: 3,
          depthHaze: 'from-slate-900/12 to-transparent',
          ambientParticles: 2,
          blueGlow: false,
        };
      case 2: // Audio Description - deeper, no surface effects
        return {
          lightRays: false,
          depthHaze: 'from-slate-950/15 to-transparent',
          ambientParticles: 1,
          blueGlow: false,
        };
      case 3: // Gumiho - deepest, nearly black
        return {
          lightRays: false,
          depthHaze: 'from-black/20 to-transparent',
          ambientParticles: 0,
          blueGlow: false,
        };
      default:
        return {
          lightRays: false,
          depthHaze: 'from-transparent to-transparent',
          ambientParticles: 0,
          blueGlow: false,
        };
    }
  };

  const depthAtmosphere = getDepthAtmosphere();

  return (
    <div ref={containerRef} className="relative py-16 lg:py-20 border-b border-cyan-900/5 last:border-b-0">
      {/* Depth-based atmosphere */}
      {depthAtmosphere.lightRays && (
        <div className="absolute top-0 left-0 right-0 h-[50vh] pointer-events-none overflow-hidden z-[1]">
          {[...Array(depthAtmosphere.lightRayCount)].map((_, i) => (
            <div
              key={i}
              className="absolute top-0 w-[1px] h-full"
              style={{
                left: `${20 + i * 20}%`,
                opacity: depthAtmosphere.lightRayOpacity,
                background: 'linear-gradient(to bottom, rgba(180,200,220,0.2) 0%, transparent 40%)',
                transform: `rotate(${-2 + i * 0.5}deg)`,
                transformOrigin: 'top',
              }}
            />
          ))}
        </div>
      )}

      {/* Depth haze overlay */}
      <div className={`absolute inset-0 bg-gradient-to-b ${depthAtmosphere.depthHaze} opacity-60 pointer-events-none z-[1]`} />

      {/* Subtle blue atmospheric glow - only at beginning of underwater journey */}
      {depthAtmosphere.blueGlow && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[800px] pointer-events-none blur-3xl z-[1]"
          style={{
            background: 'radial-gradient(circle, rgba(100,150,200,0.12) 0%, transparent 60%)',
            opacity: depthAtmosphere.blueGlowOpacity,
          }}
        />
      )}

      {/* Sparse ambient particles for depth */}
      {depthAtmosphere.ambientParticles > 0 && (
        <div className="absolute inset-0 pointer-events-none z-[2]">
          {[...Array(depthAtmosphere.ambientParticles)].map((_, i) => {
            const left = 25 + i * 30;
            const top = 20 + i * 25;
            return (
              <div
                key={i}
                className="absolute w-[2px] h-[2px] rounded-full bg-slate-400/15"
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                }}
              />
            );
          })}
        </div>
      )}

      {/* Atmospheric elements */}
      {style.showStars && <SubtleStars count={15} className="opacity-40" />}

      {/* Warm light pool */}
      <div
        className={`absolute w-[800px] h-[800px] pointer-events-none opacity-5 blur-3xl ${
          style.lightPosition === 'top-right' ? 'top-0 right-0' :
          style.lightPosition === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' :
          style.lightPosition === 'bottom-left' ? 'bottom-0 left-0' :
          'top-0 left-0'
        }`}
        style={{
          background: 'radial-gradient(circle, rgba(100,150,180,0.2) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Integrated Question + Visual Composition */}
        <div className="relative mb-editorial-md">
          {/* Project label */}
          <ScrollReveal className="mb-6">
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider">
              {project.isResearch && (
                <>
                  <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
                  <span>Research</span>
                </>
              )}
              {project.isGroupProject && (
                <>
                  <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
                  <span>Collaboration</span>
                </>
              )}
              {project.isIndependent && (
                <>
                  <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
                  <span>Independent</span>
                </>
              )}
            </div>
          </ScrollReveal>

          {/* Question overlapping with image */}
          <div className="relative z-20">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1 }}
              className="text-editorial-question font-bold text-white mb-editorial-md max-w-5xl"
              style={{
                y: questionY,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              {project.question}
            </motion.h2>
          </div>

          {/* Large hero image - starts appearing while question is still visible */}
          <motion.div
            style={{ y: imageY, scale: imageScale, opacity: imageOpacity }}
            className={`relative ${
              style.imageSize === 'large' ? 'h-[60vh] lg:h-[80vh]' :
              style.imageSize === 'medium' ? 'h-[50vh] lg:h-[60vh]' :
              'h-[40vh] lg:h-[50vh]'
            } ${
              project.title === 'Computational Fabrics for Athletes' ? 'w-full lg:w-[95%]' :
              project.title === 'Gumiho' ? 'w-full lg:w-[97%]' :
              'w-full'
            } mt-8`}
          >
            <div className="relative h-full w-full rounded overflow-hidden">
              {/* Soft mask reveal */}
              <motion.div
                className="absolute inset-0 bg-slate-950 z-10"
                initial={{ scaleX: 1 }}
                whileInView={{ scaleX: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: 'right' }}
              />

              <ImageWithFallback
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
              />

              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950/40" />
            </div>

            {/* Sticker4.2 overlay for Gumiho - top right corner, overflowing */}
            {project.title === 'Gumiho' && (
              <div className="absolute -top-20 -right-8 z-20 pointer-events-none hidden lg:block">
                <img
                  src="/images/sticker4.2.png"
                  alt=""
                  className="pointer-events-none"
                  style={{
                    width: '140px',
                    height: 'auto',
                    transform: 'rotate(8deg)',
                  }}
                />
              </div>
            )}
          </motion.div>
        </div>

        {/* Project details - layout pattern based on story */}
        {project.layoutPattern === 'minimal-visual' && (
          /* Pattern 4: Minimal Visual (Gumiho) - Keep simple so visuals dominate */
          <div className="mt-editorial-md relative">
            <ScrollReveal>
              <h3 className="text-editorial-title mb-4">{project.title}</h3>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-editorial-body text-slate-200/70 max-w-3xl leading-relaxed mb-editorial-md">
                {project.description}
              </p>
            </ScrollReveal>

            {/* Tags - simple, no cards */}
            <ScrollReveal delay={0.2}>
              <div className="flex flex-wrap gap-3">
                {project.tags?.map((tag, i) => (
                  <span key={i} className="text-editorial-meta text-cyan-400/70 uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>
            </ScrollReveal>

          </div>
        )}

        {project.layoutPattern === 'design-product' && (
          /* Pattern 3: Design-Product (ClipIt) - Product-focused presentation */
          <div className="mt-editorial-md">
            <ScrollReveal>
              <h3 className="text-editorial-title mb-4">{project.title}</h3>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-editorial-body text-slate-200/70 leading-relaxed mb-editorial-md max-w-3xl">
                {project.description}
              </p>
            </ScrollReveal>

            {/* Key insights */}
            {project.keyInsights && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-editorial-md max-w-5xl">
                {project.keyInsights.map((insight, i) => (
                  <ScrollReveal
                    key={i}
                    delay={0.2 + i * 0.1}
                  >
                    <div className="relative">
                      <div className="text-editorial-statement font-bold text-cyan-300 mb-3">
                        {insight.primary}
                      </div>
                      {/* Subtle hand-drawn arrow pointing to first stat - ClipIt only */}
                      {project.title === 'ClipIt — Language Learning Through Video' && i === 0 && (
                        <div className="absolute -left-12 top-0 hidden lg:block pointer-events-none opacity-60">
                          <SketchArrow
                            direction="right"
                            size="sm"
                            color="rgba(100, 200, 255, 0.5)"
                          />
                        </div>
                      )}
                      <div className="text-editorial-body text-slate-200/70">
                        {insight.secondary}
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            )}

            {/* Tags */}
            <ScrollReveal delay={0.4}>
              <div className="flex flex-wrap gap-3">
                {project.tags?.map((tag, i) => (
                  <span key={i} className="text-editorial-meta text-cyan-400/70 uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          </div>
        )}

        {project.layoutPattern === 'pivot-narrative' && (
          /* Pattern 2: Pivot Narrative (Audio Description) - Tease the discovery */
          <div className="mt-editorial-md">
            {/* Title */}
            <ScrollReveal>
              <h3 className="text-editorial-title mb-4">{project.title}</h3>
            </ScrollReveal>

            {/* Description */}
            <ScrollReveal delay={0.1}>
              <p className="text-editorial-body text-slate-200/70 leading-relaxed mb-editorial-md max-w-3xl">
                {project.description}
              </p>
            </ScrollReveal>

            {/* Major discovery */}
            <ScrollReveal delay={0.2} className="mb-editorial-md">
              <div className="text-editorial-statement text-cyan-300 font-semibold mb-4">
                Automation wasn't always the answer.
              </div>
              <p className="text-editorial-body text-slate-200/70 max-w-3xl">
                Most videos lacked enough appropriate silence for automatic narration insertion. This led toward a user-controlled approach that prioritizes agency over algorithmic assumptions.
              </p>
            </ScrollReveal>

            {/* Tags */}
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-3">
                {project.tags?.map((tag, i) => (
                  <span key={i} className="text-editorial-meta text-cyan-400/70 uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          </div>
        )}

        {project.layoutPattern === 'research-driven' && (
          /* Pattern 1: Research-Driven (Computational Fabrics) - Emphasize findings and statistics */
          <div className="mt-editorial-md">
            {/* Title + description */}
            <ScrollReveal>
              <h3 className="text-editorial-title mb-4">{project.title}</h3>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-editorial-body text-slate-200/70 leading-relaxed mb-editorial-md max-w-3xl">
                {project.description}
              </p>
            </ScrollReveal>

            {/* Major insights as visual moments */}
            {project.keyInsights && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-editorial-md max-w-5xl">
                {project.keyInsights.map((insight, i) => (
                  <ScrollReveal
                    key={i}
                    delay={0.2 + i * 0.1}
                  >
                    <div className="text-editorial-statement font-bold text-cyan-300 mb-3">
                      {insight.primary}
                    </div>
                    <div className="text-editorial-body text-slate-200/70">
                      {insight.secondary}
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            )}

            {/* Tags */}
            <ScrollReveal delay={0.4}>
              <div className="flex flex-wrap gap-3">
                {project.tags?.map((tag, i) => (
                  <span key={i} className="text-editorial-meta text-cyan-400/70 uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          </div>
        )}

        {/* Action buttons */}
        <ScrollReveal delay={0.5} className="mt-editorial-md">
          <div className="flex flex-wrap gap-4">
            {project.paperLink && (
              <a
                href={project.paperLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-all duration-300 font-medium hover:scale-105 shadow-lg shadow-cyan-500/20"
              >
                <FileText className="w-5 h-5" />
                Read Paper
              </a>
            )}

            {project.videoLink && !project.isResearch && (
              project.title === 'ClipIt — Language Learning Through Video' ? (
                <>
                  <Link
                    to="/projects/clipit"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-all duration-300 font-medium hover:scale-105 shadow-lg shadow-cyan-500/20"
                  >
                    <FileText className="w-5 h-5" />
                    View Case Study
                  </Link>
                  <a
                    href={project.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-slate-800/50 text-slate-200 rounded-lg hover:bg-slate-700/50 border border-cyan-900/30 transition-all duration-300 backdrop-blur-sm hover:scale-105"
                  >
                    <Video className="w-5 h-5" />
                    Watch Demo
                  </a>
                </>
              ) : (
                <a
                  href={project.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-all duration-300 font-medium hover:scale-105 shadow-lg shadow-cyan-500/20"
                >
                  <FileText className="w-5 h-5" />
                  View Case Study
                </a>
              )
            )}

            {project.videoLink && project.isResearch && (
              <a
                href={project.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-slate-800/50 text-slate-200 rounded-lg hover:bg-slate-700/50 border border-cyan-900/30 transition-all duration-300 backdrop-blur-sm hover:scale-105"
              >
                <Video className="w-5 h-5" />
                View Prototype
              </a>
            )}

            {project.demoLink && (
              <a
                href={project.demoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-slate-800/50 text-slate-200 rounded-lg hover:bg-slate-700/50 border border-cyan-900/30 transition-all duration-300 backdrop-blur-sm hover:scale-105"
              >
                <ExternalLink className="w-5 h-5" />
                {project.title === 'Gumiho' ? 'Explore Project' : 'Live Demo'}
              </a>
            )}

            {project.demoVideoLink && (
              <a
                href={project.demoVideoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-slate-800/50 text-slate-200 rounded-lg hover:bg-slate-700/50 border border-cyan-900/30 transition-all duration-300 backdrop-blur-sm hover:scale-105"
              >
                <Video className="w-5 h-5" />
                Watch Demo
              </a>
            )}
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
